/**
 * Store singleton del cliente WebRTC de Telnyx.
 * La conexión y la llamada activa viven fuera del ciclo de vida de React,
 * así la llamada no se corta al cambiar de página (SPA) ni al re-montar el layout.
 */

import { supabase } from '@/lib/supabase';
import {
  createRingbackAudioContextFromMicrophoneGesture,
  unlockWebAudioAfterMicrophoneGranted,
} from '@/lib/dtmfKeypad';
import { getUseSyntheticRingback } from '@/lib/ringbackAudio';
import {
  deriveCallUiPhaseWithCrm,
  isTerminalTelnyxState,
  normalizeTelnyxCallState,
  shouldAutoAnswerInbound,
  shouldPlaySyntheticRingbackForCallCenter,
  type TelnyxCallDirection,
  type WebrtcCallUiPhase,
} from '@/lib/telnyxCallState';
import { devLog } from '@/lib/utils';

/**
 * Telnyx `telnyx.error` suele mandar un objeto plano; `String(obj)` en UI → "[object Object]".
 */
function stringifyWebRtcError(err: unknown): string {
  if (err == null) return '';
  if (typeof err === 'string') return err.trim();
  if (err instanceof Error) return (err.message || String(err)).trim();
  if (typeof err === 'object') {
    const o = err as Record<string, unknown>;
    const inner = o.error;
    if (inner && typeof inner === 'object') {
      const n = inner as Record<string, unknown>;
      if (typeof n.message === 'string' && n.message.trim()) return n.message.trim();
    }
    for (const k of ['message', 'detail', 'description', 'reason'] as const) {
      const v = o[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
    try {
      const s = JSON.stringify(o);
      return s.length > 280 ? `${s.slice(0, 277)}…` : s;
    } catch {
      return 'Error en la conexión de voz';
    }
  }
  return String(err).trim();
}

/**
 * Errores del socket/SDK tras fin de llamada o sin contestar: el leg ya no existe en Telnyx.
 * Mostrarlos como "Error" confunde (el usuario no hizo nada mal).
 */
export function isBenignTelnyxSdkError(msg: string): boolean {
  const m = msg.trim().toLowerCase();
  if (!m) return false;
  if (m.includes('call does not exist')) return true;
  if (m.includes('no longer active')) return true;
  if (m.includes("can't receive commands")) return true;
  if (m.includes('call leg not found')) return true;
  if (m.includes('unknown call')) return true;
  if (m.includes('call not found')) return true;
  if (m.includes('transaction does not exist')) return true;
  return false;
}

function isInCallFlow(state: TelnyxWebRTCState): boolean {
  return state.callPhase !== 'none' || (state.activeCallId != null && state.activeCallId !== '');
}

/** Texto seguro para Banners (evita `[object Object]` si algo no-string llegara al estado). */
export function formatWebRtcErrorForUi(value: string | null | undefined | unknown): string | null {
  if (value == null || value === '') return null;
  const raw = typeof value === 'string' ? value.trim() : stringifyWebRtcError(value);
  if (!raw) return null;
  if (isBenignTelnyxSdkError(raw)) return null;
  return raw;
}

export type WebrtcStatus = 'idle' | 'connecting' | 'connected' | 'error';

const STORAGE_KEY = 'call_center_webrtc_enabled';

/** Número marcado / destino conocido por la UI antes de que Telnyx exponga metadatos (E.164 preferido). */
let pendingDialNumber: string | null = null;

/**
 * Evita consultas duplicadas a `crm_calls` por `client_state` (mismo pedido COD/ecommerce).
 * Se limpia en `clearDialContext` / fin de llamada.
 */
let clientStateCrmHydrateInFlight: string | null = null;

export type TelnyxWebRTCState = {
  status: WebrtcStatus;
  error: string | null;
  activeCallId: string | null;
  webrtcUsername: string | null;
  /** Timbrando / conectando vs en conversación. */
  callPhase: WebrtcCallUiPhase;
  callDirection: TelnyxCallDirection | null;
  /** Teléfono mostrado en la tarjeta CRM (origen/destino). */
  remoteDialNumber: string | null;
  /** Estado crudo normalizado (string). */
  telnyxCallState: string;
  /** Tono local de timbrado (solo saliente en fase new/trying/requesting/recovering). */
  playLocalRingback: boolean;
  /** Fila `crm_calls.id` de la llamada actual (tras start-call). */
  activeCrmCallId: string | null;
  /** `crm_calls.call_status` (webhook). Sirve mientras el SDK ya está `active` y el PSTN sigue timbrando. */
  crmCallStatus: string | null;
  /** Micrófono silenciado (local). */
  isMuted: boolean;
  /** Llamada en espera (hold SIP). */
  isOnHold: boolean;
}

type Listener = (state: TelnyxWebRTCState) => void;

interface TelnyxCallRef {
  hangup?: () => void;
  hold?: () => unknown;
  unhold?: () => unknown;
  muteAudio?: () => void;
  unmuteAudio?: () => void;
  deaf?: () => void;
  undeaf?: () => void;
}

interface Store {
  client: unknown;
  activeCallRef: TelnyxCallRef | null;
  state: TelnyxWebRTCState;
  listeners: Set<Listener>;
}

const initialCallFields = {
  callPhase: 'none' as WebrtcCallUiPhase,
  callDirection: null as TelnyxCallDirection | null,
  remoteDialNumber: null as string | null,
  telnyxCallState: '',
  playLocalRingback: false,
  activeCrmCallId: null as string | null,
  crmCallStatus: null as string | null,
  isMuted: false,
  isOnHold: false,
};

const initialState: TelnyxWebRTCState = {
  status: 'idle',
  error: null,
  activeCallId: null,
  webrtcUsername: null,
  ...initialCallFields,
};

const store: Store = {
  client: null,
  activeCallRef: null,
  state: { ...initialState },
  listeners: new Set(),
};

function getState(): TelnyxWebRTCState {
  return { ...store.state };
}

function setState(partial: Partial<TelnyxWebRTCState>): void {
  let changed = false;
  for (const key of Object.keys(partial) as (keyof TelnyxWebRTCState)[]) {
    if (store.state[key] !== (partial as TelnyxWebRTCState)[key]) {
      (store.state as Record<string, unknown>)[key] = (partial as Record<string, unknown>)[key];
      changed = true;
    }
  }
  if (changed) {
    const next = getState();
    store.listeners.forEach((fn) => {
      try {
        fn(next);
      } catch (e) {
        devLog.error('telnyxWebRTCStore listener error:', e);
      }
    });
  }
}

/**
 * Llamar tras `call-center-start-call` con éxito para mostrar CRM y tono de timbrado acorde al número.
 * Muestra la tarjeta de llamada de inmediato (no depende solo de eventos SDK).
 */
export function setDialContext(e164OrRaw: string | null): void {
  const n = e164OrRaw?.trim() || null;
  pendingDialNumber = n;
  if (n) {
    setState({
      remoteDialNumber: n,
      callPhase: 'ringing',
      callDirection: 'outbound',
      activeCallId: 'pending',
      telnyxCallState: 'outbound_ui_pending',
      playLocalRingback: getUseSyntheticRingback(),
    });
  } else {
    setState({ remoteDialNumber: null });
  }
}

/**
 * Cuando el número aún no está (p. ej. click-to-call con `lead_id` y fetch async), abre la UI al instante.
 * Luego `setDialContext(tel)` completará el número.
 */
export function beginCallUiFromApp(): void {
  /** Restaurar <audio> remoto por si quedó muted+paused de la llamada anterior. */
  unmuteRemoteAudioElement();
  setState({
    callPhase: 'ringing',
    callDirection: 'outbound',
    activeCallId: 'pending',
    telnyxCallState: 'outbound_ui_pending',
    playLocalRingback: getUseSyntheticRingback(),
  });
}

function clearDialContext(): void {
  pendingDialNumber = null;
  clientStateCrmHydrateInFlight = null;
}

/**
 * Realtime a veces no está habilitado en `crm_calls`, o `agent_id` en BD ≠ usuario logueado (mismo SIP).
 * Telnyx suele enviar en la sesión WebRTC el `client_state` (base64) con `cod_order_id` / `ecommerce_order_id`.
 * Resolviendo `crm_calls` por ese id (RLS por tenant) enlazamos FAB + teléfono sin depender del filtro por agente.
 */
function coerceClientStateOrderId(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return '';
}

function storeLooksLikeActiveCallSession(): boolean {
  const st = store.state;
  if (st.callPhase !== 'none') return true;
  const id = st.activeCallId;
  return id != null && id !== '' && id !== 'pending';
}

function scheduleHydrateCrmCallFromTelnyxClientState(callRec: Record<string, unknown>): void {
  if (store.state.activeCrmCallId) return;
  const j = decodeTelnyxClientStateJson(callRec);
  if (!j) return;
  const codId = coerceClientStateOrderId(j.cod_order_id);
  const ecomId = coerceClientStateOrderId(j.ecommerce_order_id);
  if (!codId && !ecomId) return;
  const key = codId ? `cod:${codId}` : `ecom:${ecomId}`;
  if (clientStateCrmHydrateInFlight === key) return;
  clientStateCrmHydrateInFlight = key;

  void (async () => {
    try {
      const since = new Date(Date.now() - 300_000).toISOString();
      let q = supabase
        .from('crm_calls')
        .select('id, destination_phone, call_status')
        .in('call_status', ['pending', 'initiated', 'ringing', 'answered'])
        .gte('created_at', since);
      if (codId) q = q.eq('cod_order_id', codId);
      else q = q.eq('ecommerce_order_id', ecomId);
      const { data, error } = await q.order('created_at', { ascending: false }).limit(1).maybeSingle();
      if (error || !data?.id) return;
      const st = getState();
      if (st.activeCrmCallId) return;
      if (!storeLooksLikeActiveCallSession()) return;
      const dest = (data.destination_phone as string | null | undefined)?.trim() || null;
      setActiveCrmCall(data.id, (data.call_status as string | null | undefined) ?? 'initiated', {
        forceBusinessOutbound: true,
      });
      if (dest) mergeOutboundDestinationPhone(dest);
    } finally {
      if (clientStateCrmHydrateInFlight === key) clientStateCrmHydrateInFlight = null;
    }
  })();
}

/**
 * Solo `pendingDialNumber` + `remoteDialNumber`. **No** resetea `activeCallId` ni fase.
 * Necesario cuando WebRTC ya enganchó y llega Realtime/`crm_calls` con el PSTN real: `setDialContext` rompería el SDK.
 */
export function mergeOutboundDestinationPhone(e164OrRaw: string | null | undefined): void {
  const n = e164OrRaw?.trim() || null;
  if (!n) return;
  pendingDialNumber = n;
  setState({ remoteDialNumber: n });
}

/** Tras `call-center-start-call` o Realtime: enlaza `crm_calls` para timbrado y cierre al colgar el remoto. */
export function setActiveCrmCall(
  callId: string | null,
  initialCrmStatus?: string | null,
  opts?: { forceBusinessOutbound?: boolean },
): void {
  if (!callId) {
    setState({ activeCrmCallId: null, crmCallStatus: null });
    return;
  }
  const init = (initialCrmStatus?.trim() || 'initiated').toLowerCase();
  const st = store.state;
  const directionForPhase: TelnyxCallDirection | null =
    opts?.forceBusinessOutbound === true ? 'outbound' : st.callDirection;
  const phase = deriveCallUiPhaseWithCrm(st.telnyxCallState, init, directionForPhase);
  const playLocal =
    getUseSyntheticRingback() &&
    shouldPlaySyntheticRingbackForCallCenter(st.telnyxCallState, init, callId, phase, directionForPhase);
  setState({
    activeCrmCallId: callId,
    crmCallStatus: init,
    callPhase: phase,
    playLocalRingback: playLocal,
    ...(opts?.forceBusinessOutbound ? { callDirection: 'outbound' as const } : {}),
  });

  const idTrim = callId.trim();
  void supabase
    .from('crm_calls')
    .select('destination_phone')
    .eq('id', idTrim)
    .maybeSingle()
    .then(({ data, error }) => {
      if (error || !data) return;
      const p = (data as { destination_phone?: string | null }).destination_phone?.trim();
      if (!p) return;
      const digits = p.replace(/\D/g, '');
      const noPlus = p.startsWith('+') ? p.slice(1) : p;
      if (digits.length < 8 || /[a-zA-Z]/.test(noPlus)) return;
      mergeOutboundDestinationPhone(p);
    });
}

/** Actualiza estado CRM desde Realtime (webhook) y recalcula fase UI + timbrado. */
export function setCrmCallStatusFromRealtime(status: string | null): void {
  const st = store.state;
  const crmLower = status?.trim().toLowerCase() ?? null;
  const phase = deriveCallUiPhaseWithCrm(st.telnyxCallState, crmLower, st.callDirection);
  const playLocal =
    getUseSyntheticRingback() &&
    shouldPlaySyntheticRingbackForCallCenter(
      st.telnyxCallState,
      crmLower,
      st.activeCrmCallId,
      phase,
      st.callDirection,
    );
  setState({ crmCallStatus: crmLower, callPhase: phase, playLocalRingback: playLocal });
}

// H1 I5 (parcial): codec extraido a src/lib/telnyxClientStateCodec.ts.
// Las exports siguen disponibles via re-export para callers existentes.
import { decodeTelnyxClientStateJson, callLooksLikeOutboundCampaign } from './telnyxClientStateCodec';

/**
 * El SDK suele poner en caller/remote un **usuario SIP** (`userluanfuentes0164388`), no el PSTN.
 * Solo aceptamos cadenas que parezcan teléfono para no pisar `pendingDialNumber` / `client_state.to_number`.
 */
function isPlausiblePstnDisplay(s: string | null | undefined): boolean {
  const t = s?.trim() ?? '';
  if (t.length < 8) return false;
  const noPlus = t.startsWith('+') ? t.slice(1) : t;
  if (/[a-zA-Z]/.test(noPlus)) return false;
  const digits = t.replace(/\D/g, '');
  return digits.length >= 8;
}

function toNumberFromTelnyxClientState(call: Record<string, unknown>): string | null {
  const j = decodeTelnyxClientStateJson(call);
  const raw = j && typeof j.to_number === 'string' ? j.to_number.trim() : '';
  return raw || null;
}

function extractRemoteNumberFromCall(call: {
  state?: string | number;
  direction?: string;
}): string | null {
  const c = call as Record<string, unknown>;
  const opt = (c.options ?? c.params) as Record<string, unknown> | undefined;
  if (opt && typeof opt === 'object') {
    for (const k of ['destinationNumber', 'destination_number', 'callerNumber', 'caller_number', 'remoteNumber']) {
      const v = opt[k];
      if (typeof v === 'string' && v.trim()) {
        const t = v.trim();
        if (isPlausiblePstnDisplay(t)) return t;
      }
    }
  }
  for (const k of ['destinationNumber', 'remoteNumber', 'callerNumber']) {
    const v = c[k];
    if (typeof v === 'string' && v.trim()) {
      const t = v.trim();
      if (isPlausiblePstnDisplay(t)) return t;
    }
  }
  return null;
}

function applyCallUpdate(call: {
  id?: string;
  state?: string | number;
  direction?: string;
  answer?: () => Promise<void>;
  hangup?: () => void;
}): void {
  const stateNorm = normalizeTelnyxCallState(call.state);
  devLog.log('[applyCallUpdate] SDK state:', call.state, '→ normalized:', stateNorm, '| phase:', store.state.callPhase, '| id:', call.id?.slice(0, 12));
  /**
   * **SDK `direction` vs negocio (CRM):** en el bridge WebRTC del call center, Telnyx suele crear una
   * llamada **entrante al navegador** (`direction: inbound`) aunque el agente esté **marcando al lead**
   * (saliente en CRM). Antes priorizábamos `inbound` y la UI decía «Entrante», sin timbrado local y
   * con fase/conversación mal alineadas.
   *
   * - `businessDir`: etiqueta CRM, timbrado, `deriveCallUiPhaseWithCrm` (saliente + CRM hasta `answered`).
   * - `legDir`: pata SIP que ve el SDK; para contestar automáticamente el timbre **al navegador** debe
   *   seguir siendo `inbound` cuando el proveedor dice inbound, aunque `businessDir` sea `outbound`.
   */
  const rawDir = (call.direction as string | undefined)?.toLowerCase();
  const st = store.state;
  const callRec = call as Record<string, unknown>;
  /**
   * Carrera Telnyx vs Realtime: el SDK puede marcar `inbound` al navegador antes del INSERT en el cliente.
   * En este producto la pata WebRTC del call center es casi siempre bridge de una saliente; si la sesión
   * estaba idle y llega inbound en timbrado/conexión, tratamos como saliente de negocio hasta que exista
   * flujo real de PSTN entrante al softphone (entonces habría que acotar esta heurística).
   */
  /**
   * Incluir `answering` y estado vacío: el SDK suele mandar esos valores **antes** del enum en `ringing`/`trying`
   * y antes del INSERT Realtime de `crm_calls`. Si no los tratamos como bridge, `businessDir` queda `inbound`,
   * `shouldPlaySyntheticRingbackForCallCenter` devuelve false y el “tuuu” nunca arranca (auto-llamada con FAB cerrado).
   * `connecting`: misma sesión SIP puede recibir la oferta mientras el store aún no pasó a `connected`.
   */
  const sdkInboundBridgeWhileIdle =
    rawDir === 'inbound' &&
    (st.activeCallId == null || st.activeCallId === '') &&
    (st.status === 'connected' || st.status === 'connecting') &&
    (!stateNorm.trim() ||
      stateNorm === 'ringing' ||
      stateNorm === 'new' ||
      stateNorm === 'trying' ||
      stateNorm === 'requesting' ||
      stateNorm === 'early' ||
      stateNorm === 'recovering' ||
      stateNorm === 'answering');
  const outboundBridgeIntent =
    callLooksLikeOutboundCampaign(callRec) ||
    pendingDialNumber != null ||
    st.telnyxCallState === 'outbound_ui_pending' ||
    st.activeCrmCallId != null ||
    st.callDirection === 'outbound' ||
    sdkInboundBridgeWhileIdle;

  let businessDir: TelnyxCallDirection;
  if (outboundBridgeIntent) {
    businessDir = 'outbound';
  } else if (rawDir === 'outbound') {
    businessDir = 'outbound';
  } else if (rawDir === 'inbound') {
    businessDir = 'inbound';
  } else {
    businessDir = 'inbound';
  }

  const legDir: TelnyxCallDirection =
    rawDir === 'outbound' ? 'outbound' : rawDir === 'inbound' ? 'inbound' : outboundBridgeIntent ? 'inbound' : 'inbound';

  if (isTerminalTelnyxState(stateNorm)) {
    muteRemoteAudioElement();
    store.activeCallRef = null;
    clearDialContext();
    setState({
      activeCallId: null,
      ...initialCallFields,
    });
    return;
  }

  /**
   * A veces el SDK envía un update sin `state` al colgar el remoto; sin `state` la fase sería `ringing` y el cronómetro seguiría.
   * Si ya teníamos una llamada activa (en conversación O timbrando), interpretar como fin.
   */
  if (!stateNorm.trim()) {
    const hadActiveCall =
      store.state.callPhase === 'active' ||
      store.state.callPhase === 'ringing' ||
      store.state.telnyxCallState === 'active' ||
      store.state.telnyxCallState === 'held';
    if (hadActiveCall) {
      devLog.log('[applyCallUpdate] empty state while call active/ringing → clearing');
      store.activeCallRef = null;
      clearDialContext();
      setState({
        activeCallId: null,
        ...initialCallFields,
        error: null,
      });
      return;
    }
  }

  store.activeCallRef = call;
  const id = (call.id as string | undefined) ?? 'active';

  /** Restaurar el <audio> remoto: pudo haber quedado muted+paused de la llamada anterior. */
  unmuteRemoteAudioElement();

  if (shouldAutoAnswerInbound(stateNorm, legDir)) {
    try {
      if (typeof call.answer === 'function') void call.answer();
    } catch (e) {
      devLog.error('WebRTC answer error:', e);
    }
  }

  const phase = deriveCallUiPhaseWithCrm(stateNorm, st.crmCallStatus, businessDir);
  const playLocal =
    getUseSyntheticRingback() &&
    shouldPlaySyntheticRingbackForCallCenter(
      stateNorm,
      st.crmCallStatus,
      st.activeCrmCallId,
      phase,
      businessDir,
    );
  const fromCall = extractRemoteNumberFromCall(call);
  /** Auto-llamada / bridge: Call Control manda `to_number` en `client_state` (Edge `ecommerce-order-auto-call`). */
  const fromClientState = toNumberFromTelnyxClientState(callRec);
  let remote =
    businessDir === 'inbound'
      ? fromCall ?? pendingDialNumber ?? fromClientState ?? null
      : pendingDialNumber ?? fromClientState ?? fromCall ?? null;

  /** No sustituir un PSTN bueno (Realtime/BD) por el usuario SIP del SDK en actualizaciones sucesivas. */
  if (!isPlausiblePstnDisplay(remote)) {
    if (isPlausiblePstnDisplay(st.remoteDialNumber)) {
      remote = st.remoteDialNumber;
    } else if (isPlausiblePstnDisplay(pendingDialNumber ?? undefined)) {
      remote = pendingDialNumber;
    }
  }

  setState({
    activeCallId: id,
    callPhase: phase,
    callDirection: businessDir,
    telnyxCallState: stateNorm,
    playLocalRingback: playLocal,
    remoteDialNumber: remote,
  });

  scheduleHydrateCrmCallFromTelnyxClientState(callRec);
}

export function subscribe(listener: Listener): () => void {
  store.listeners.add(listener);
  return () => {
    store.listeners.delete(listener);
  };
}

export function getWebRTCState(): TelnyxWebRTCState {
  return getState();
}

export async function connect(): Promise<void> {
  if (store.client != null) {
    setState({ status: 'connected', error: null });
    return;
  }
  setState({ error: null, status: 'connecting' });
  try {
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    const mediaDevices = nav?.mediaDevices;
    if (!mediaDevices || typeof mediaDevices.getUserMedia !== 'function') {
      const origin = typeof location !== 'undefined' ? location.origin : '';
      const isLocalhost = /^(https?:\/\/)?(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(origin);
      const isSmartyLocal = /smarty\.local/i.test(origin);
      let msg: string;
      if (isSmartyLocal && !origin.startsWith('https:'))
        msg =
          'Con web.smarty.local el micrófono solo funciona por HTTPS. Ejecuta "npm run dev:https" y abre https://web.smarty.local:8080 (acepta el certificado si el navegador lo pide).';
      else if (isLocalhost)
        msg =
          'El micrófono no está disponible. Abre la app con http://localhost:8080 o http://127.0.0.1:8080 (no uses la IP de la red). Si ya lo haces, revisa que el navegador permita el micrófono para este sitio.';
      else
        msg =
          'El micrófono requiere contexto seguro. En local usa http://localhost:8080 o https con "npm run dev:https"; en producción usa HTTPS.';
      setState({ error: msg, status: 'error' });
      return;
    }
    let testStream: MediaStream | null = null;
    try {
      testStream = await mediaDevices.getUserMedia({ audio: true, video: false });
      /**
       * Contexto **dedicado** al timbrado en la cadena del micrófono + desbloqueo del contexto DTMF compartido.
       * Así el “tuuu” no depende del mismo `AudioContext` que pudo crearse antes (teclado) y quedar bloqueado.
       */
      createRingbackAudioContextFromMicrophoneGesture();
      unlockWebAudioAfterMicrophoneGranted();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setState({
        error:
          msg.includes('Permission') || msg.includes('denied') || msg.includes('NotAllowed')
            ? 'Se necesita permiso de micrófono para llamar por el navegador. Actívalo en la configuración del sitio.'
            : `No se pudo acceder al micrófono: ${msg}`,
        status: 'error',
      });
      return;
    } finally {
      testStream?.getTracks().forEach((t) => t.stop());
    }
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) {
      setState({ error: 'Sesión expirada. Inicia sesión de nuevo.', status: 'error' });
      return;
    }
    const { data, error: fnError } = await supabase.functions.invoke('call-center-webrtc-token', {
      headers: { Authorization: `Bearer ${token}` },
    });
    const res = (data ?? {}) as { success?: boolean; login?: string; password?: string; error?: string; message?: string };
    if (fnError || !res?.success || !res?.login || !res?.password) {
      setState({
        error: res?.message || res?.error || fnError?.message || 'No se pudo obtener credenciales WebRTC.',
        status: 'error',
      });
      return;
    }
    const { TelnyxRTC } = await import('@telnyx/webrtc');
    const client = new TelnyxRTC({
      login: res.login,
      password: res.password,
    });
    (client as { remoteElement?: string }).remoteElement = 'telnyx-remote-audio';
    store.client = client;
    setState({ webrtcUsername: res.login.trim() });

    client.on('telnyx.ready', () => {
      setState({ status: 'connected', error: null });
      /** Sesión SIP lista: reintenta desbloqueo de ambos contextos (timbrado dedicado + DTMF). */
      createRingbackAudioContextFromMicrophoneGesture();
      unlockWebAudioAfterMicrophoneGranted();
      try {
        localStorage.setItem(STORAGE_KEY, '1');
      } catch {
        // ignore
      }
    });
    client.on('telnyx.error', (err: unknown) => {
      const msg = stringifyWebRtcError(err);
      if (isBenignTelnyxSdkError(msg)) {
        devLog.warn('telnyx.error (benigno, sin UI):', msg);
        const cur = store.state;
        if (
          store.client != null &&
          cur.status === 'error' &&
          (cur.error == null || isBenignTelnyxSdkError(cur.error))
        ) {
          setState({ error: null, status: 'connected' });
        }
        return;
      }

      const st = store.state;
      /** Durante timbrado/conversación el SDK a veces emite ruido: no tumbar `connected` ni llenar el banner. */
      if (isInCallFlow(st)) {
        if (msg) devLog.warn('telnyx.error (omitido en UI durante llamada):', msg);
        return;
      }

      setState({ error: msg || 'Error de conexión WebRTC', status: 'error' });
    });
    client.on(
      'telnyx.notification',
      (notification: {
        type?: string;
        call?: {
          state?: string | number;
          id?: string;
          direction?: string;
          answer?: () => Promise<void>;
          hangup?: () => void;
        };
      }) => {
        const call = notification?.call;
        if (!call) return;
        // No filtrar solo por `callUpdate`: el SDK puede emitir otras notificaciones con el mismo `call`.
        applyCallUpdate(call);
      }
    );

    const c = client as { enableMicrophone?: () => void };
    if (typeof c.enableMicrophone === 'function') c.enableMicrophone();
    client.connect();
  } catch (e) {
    setState({
      error: stringifyWebRtcError(e) || 'Error al conectar WebRTC',
      status: 'error',
    });
  }
}

export function disconnect(): void {
  try {
    const client = store.client as { disconnect?: () => void } | null;
    if (client?.disconnect) client.disconnect();
  } catch {
    // ignore
  }
  store.client = null;
  store.activeCallRef = null;
  clearDialContext();
  setState({
    ...initialState,
  });
}

export function disconnectAndDisable(): void {
  try {
    localStorage.setItem(STORAGE_KEY, '0');
  } catch {
    // ignore
  }
  disconnect();
}

/** Silencia el `<audio>` remoto inmediatamente para cortar audio residual (WAV de ringback, etc.).
 *  NO tocar `srcObject` — el SDK de Telnyx lo gestiona y anularlo rompe futuras llamadas. */
export function muteRemoteAudioElement(): void {
  try {
    const el = document.getElementById('telnyx-remote-audio') as HTMLAudioElement | null;
    if (el) {
      el.muted = true;
      el.pause();
    }
  } catch {
    // ignore — SSR o elemento no existe
  }
}

/** Restaura el `<audio>` remoto al iniciar una nueva llamada (deshace `muteRemoteAudioElement`). */
function unmuteRemoteAudioElement(): void {
  try {
    const el = document.getElementById('telnyx-remote-audio') as HTMLAudioElement | null;
    if (el) {
      el.muted = false;
      el.play().catch(() => {});
    }
  } catch {
    // ignore — SSR o elemento no existe
  }
}

/* ───────── Hold / Mute ───────── */

export function toggleMute(): boolean {
  const call = store.activeCallRef;
  if (!call) return store.state.isMuted;
  const next = !store.state.isMuted;
  try {
    if (next) {
      call.muteAudio?.();
    } else {
      call.unmuteAudio?.();
    }
  } catch {
    // ignore — SDK puede fallar si la llamada ya terminó
  }
  setState({ isMuted: next });
  return next;
}

export function toggleHold(): boolean {
  const call = store.activeCallRef;
  if (!call) return store.state.isOnHold;
  const next = !store.state.isOnHold;
  try {
    if (next) {
      call.hold?.();
    } else {
      call.unhold?.();
    }
  } catch {
    // ignore
  }
  setState({ isOnHold: next });
  return next;
}

export function hangup(): void {
  const call = store.activeCallRef;
  /** Primero el SDK (Telnyx WebRTC); si limpiamos el estado antes, el tono/remoto puede seguir activo. */
  if (call?.hangup) {
    try {
      call.hangup();
    } catch {
      // ignore
    }
  }
  muteRemoteAudioElement();
  store.activeCallRef = null;
  clearDialContext();
  setState({ activeCallId: null, ...initialCallFields, error: null });
}

export function clearActiveCallFromRemote(): void {
  muteRemoteAudioElement();
  store.activeCallRef = null;
  clearDialContext();
  setState({ activeCallId: null, ...initialCallFields, error: null });
}

export function isStorageEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}
